import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export type TargetExamModule = 'concursos' | 'oab' | 'enem' | 'tribunais' | 'carreiras-policiais' | 'carreiras-juridicas' | 'cnu' | 'bancario' | 'militares' | 'residencia' | 'colegios' | 'vestibulares';

export interface TargetExam {
  id: string;
  user_id: string;
  module_type: TargetExamModule;
  name: string;
  exam_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface TargetExamInput {
  name: string;
  exam_date?: string | null;
  notes?: string | null;
}

export function useTargetExams(moduleType: TargetExamModule) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const queryKey = ['target-exams', user?.id, moduleType];

  const query = useQuery({
    queryKey,
    enabled: !!user,
    queryFn: async (): Promise<TargetExam[]> => {
      const { data, error } = await supabase
        .from('user_target_exams')
        .select('*')
        .eq('user_id', user!.id)
        .eq('module_type', moduleType)
        .order('exam_date', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as TargetExam[];
    },
  });

  const add = useMutation({
    mutationFn: async (input: TargetExamInput) => {
      if (!user) throw new Error('Não autenticado');
      const { error } = await supabase.from('user_target_exams').insert({
        user_id: user.id,
        module_type: moduleType,
        name: input.name.trim(),
        exam_date: input.exam_date || null,
        notes: input.notes || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      toast.success('Concurso alvo adicionado');
    },
    onError: () => toast.error('Erro ao adicionar concurso'),
  });

  const update = useMutation({
    mutationFn: async ({ id, ...input }: TargetExamInput & { id: string }) => {
      const { error } = await supabase
        .from('user_target_exams')
        .update({
          name: input.name.trim(),
          exam_date: input.exam_date || null,
          notes: input.notes || null,
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      toast.success('Concurso alvo atualizado');
    },
    onError: () => toast.error('Erro ao atualizar concurso'),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('user_target_exams').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      toast.success('Concurso alvo removido');
    },
    onError: () => toast.error('Erro ao remover concurso'),
  });

  return {
    exams: query.data ?? [],
    isLoading: query.isLoading,
    add,
    update,
    remove,
  };
}
